import { useContext, useMemo } from 'react';
import { useSplitTreatments } from '@splitsoftware/splitio-react';
import { FrontendConfigContext } from '../providers/FrontendConfigProvider';
import { AuthContext } from '../providers/AuthProvider';

type FeatureFlags = {
  sidebarType: string;
  sidebarFeatures: string[];
  aiEnabledAPI: any[];
  devxGroupBaseEnable: boolean;
  isReady: boolean;
};

const DEFAULT_FEATURE_FLAGS: FeatureFlags = {
  sidebarType: '',
  sidebarFeatures: [],
  aiEnabledAPI: [],
  devxGroupBaseEnable: false,
  isReady: false,
};

export const useFeatureFlags = (controlledItems?: any[]): FeatureFlags => {
  const { frontendConfigData } = useContext(FrontendConfigContext);
  const { profEmail } = useContext(AuthContext);
  const splitTreatments = useSplitTreatments({
    names: ['devx-ui-feature-enable'],
    splitKey: profEmail?.toLowerCase() || 'anonymous',
  });

  const isLocalDevelopment =
    typeof window !== 'undefined' &&
    ['localhost', '127.0.0.1'].includes(window.location.hostname);

  return useMemo(() => {
    console.log('treatments:', splitTreatments?.treatments);
    if (isLocalDevelopment && controlledItems) {
      return {
        ...DEFAULT_FEATURE_FLAGS,
        sidebarFeatures: controlledItems.map(item => item.label || item.name),
        isReady: true,
      };
    }

    if (!frontendConfigData) {
      return DEFAULT_FEATURE_FLAGS;
    }

    const splitConfig =
      splitTreatments?.treatments?.['devx-ui-feature-enable']?.config;

    if (!splitConfig) {
      // Use splitTreatments.isReady to determine if the feature flags are ready, even if the specific config is not available
      return {
        ...DEFAULT_FEATURE_FLAGS,
        isReady: splitTreatments?.isReady ?? false,
      };
    }

    try {
      const parsedConfig = JSON.parse(splitConfig);
      return {
        sidebarType:
          typeof parsedConfig?.sidebarType === 'string'
            ? parsedConfig.sidebarType
            : '',
        sidebarFeatures: Array.isArray(parsedConfig?.sidebarFeatures)
          ? parsedConfig.sidebarFeatures
          : [],
        aiEnabledAPI: Array.isArray(parsedConfig?.aiEnabledAPI)
          ? parsedConfig.aiEnabledAPI
          : [],
        devxGroupBaseEnable:
          typeof parsedConfig?.['devx-group-base-enable'] === 'boolean'
            ? parsedConfig['devx-group-base-enable']
            : false,
        isReady: true,
      };
    } catch {
      return DEFAULT_FEATURE_FLAGS;
    }
  }, [
    frontendConfigData,
    splitTreatments?.treatments,
    splitTreatments?.isReady,
    isLocalDevelopment,
    controlledItems,
  ]);
};
